import type { ChartData } from 'chart.js'
import {
  ChannelKey,
  DrillDimension,
  FindingKind,
  IChatManagementReport,
  IDrillSelection,
  IIntentRow,
  SuccessTreatment,
} from './models'

/**
 * Anlamsal renkler (spec §8.5; validate_palette.js ile doğrulandı). Renk varlığı izler, sıralamayı değil:
 * mavi = başarı, aqua = veri yok, sarı = netleştirme, mor = bot doğru, turuncu = başarısız, gri = gürültü.
 */
export const PALETTE = {
  success: '#2a78d6',
  noData: '#1baf7a',
  clarification: '#eda100',
  correctBehavior: '#4a3aa7',
  improvement: '#eb6834',
  noise: '#898781',
  offHours: '#86b6ef',
  surface: '#ffffff',
} as const

const CATEGORY_COLORS: Record<string, string> = {
  success: PALETTE.success,
  noData: PALETTE.noData,
  clarification: PALETTE.clarification,
  correctBehavior: PALETTE.correctBehavior,
  improvement: PALETTE.improvement,
  noise: PALETTE.noise,
}

const TREATMENT_COLORS: Record<SuccessTreatment, string> = {
  counted: PALETTE.success,
  notCounted: PALETTE.improvement,
  excluded: PALETTE.noise,
}

export const treatmentColor = (treatment: SuccessTreatment): string => TREATMENT_COLORS[treatment] ?? PALETTE.noise

const oneDecimal = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })
const wholeNumber = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 })
const shortWeekday = new Intl.DateTimeFormat('tr-TR', { weekday: 'short' })
const pad = (value: number): string => String(value).padStart(2, '0')

export const formatPercent = (value: number): string => `%${oneDecimal.format(value)}`

/** Spec §4.3: paydası 0 olan satırın oranını sunucu 0 yollar; ekran "%0" yerine bunu gösterir. */
export const NO_RATE_TEXT = '—'
export const formatCount = (value: number): string => wholeNumber.format(value)
export const formatDecimal = (value: number): string => oneDecimal.format(value)
export const formatHour = (hour: number): string => `${pad(hour)}:00`

/** "yyyy-MM-dd" yerel tarih; sunucu zaten Türkiye günü verdiği için saat dilimi dönüşümü yapılmaz. */
export const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export const formatLocalDate = (value: string): string => {
  const date = parseLocalDate(value)
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
}

export const formatLocalDay = (value: string): string =>
  `${formatLocalDate(value)} ${shortWeekday.format(parseLocalDate(value))}`

export const toInputDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

export const fromInputDate = (value: string): Date | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseLocalDate(value) : null

export const startOfLocalDay = (date: Date): Date => {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  return start
}

export const endExclusiveOfLocalDay = (date: Date): Date => {
  const end = startOfLocalDay(date)
  end.setDate(end.getDate() + 1)
  return end
}

export const successTreatmentLabel = (treatment: SuccessTreatment): string =>
  treatment === 'counted' ? 'Evet' : treatment === 'notCounted' ? 'Hayır' : 'Paydada değil'

export const FINDING_LABELS: Record<FindingKind, string> = {
  genel: 'Genel',
  sorun: 'Sorun',
  firsat: 'Fırsat',
  guclu: 'Güçlü',
  kullanim: 'Kullanım',
  kanal: 'Kanal',
  olcum: 'Ölçüm',
  veri: 'Veri',
}

export type FindingTone = 'critical' | 'warning' | 'good' | 'neutral'

/** Durum renkleri yalnız durum anlamı taşıyan bulgulardadır; her zaman simge + etiketle gelir. */
export const findingTone = (kind: FindingKind): FindingTone =>
  kind === 'sorun' ? 'critical' : kind === 'firsat' ? 'warning' : kind === 'guclu' ? 'good' : 'neutral'

export const FINDING_ICONS: Record<FindingTone, string> = { critical: '!', warning: '↗', good: '✓', neutral: 'i' }

export type DeltaTone = 'good' | 'bad' | 'neutral'

export interface KpiDelta {
  text: string
  direction: 'up' | 'down' | 'flat' | 'none'
  tone: DeltaTone
}

const NO_PREVIOUS: KpiDelta = { text: 'önceki dönemde veri yok', direction: 'none', tone: 'neutral' }
const roundOne = (value: number): number => Math.round(value * 10) / 10

/** Oranlarda puan farkı, sayılarda yüzde değişim (spec §8.5). Renk tek başına anlam taşımaz: ok + metin. */
export const buildDelta = (
  current: number,
  previous: number,
  previousTurns: number,
  kind: 'rate' | 'count',
  higherIsBetter: boolean
): KpiDelta => {
  if (previousTurns === 0 || (kind === 'count' && previous === 0)) return NO_PREVIOUS

  const change = kind === 'rate' ? roundOne(current - previous) : roundOne(((current - previous) / previous) * 100)
  if (!Number.isFinite(change)) return NO_PREVIOUS
  if (change === 0) return { text: 'değişim yok', direction: 'flat', tone: 'neutral' }

  const sign = change > 0 ? '+' : '-'
  const magnitude = oneDecimal.format(Math.abs(change))
  const increased = change > 0
  return {
    text: kind === 'rate' ? `${sign}${magnitude} puan` : `${sign}%${magnitude}`,
    direction: change > 0 ? 'up' : 'down',
    tone: increased === higherIsBetter ? 'good' : 'bad',
  }
}

export interface KpiCardModel {
  key: string
  label: string
  value: string
  caption: string
  delta: KpiDelta
  trend?: number[]
}

export const buildKpiCards = (report: IChatManagementReport): KpiCardModel[] => {
  const current = report.Kpis
  const previous = report.PreviousKpis
  const previousTurns = previous.TotalTurns

  return [
    {
      key: 'totalTurns',
      label: 'Toplam Soru',
      value: formatCount(current.TotalTurns),
      caption: 'tur',
      delta: buildDelta(current.TotalTurns, previous.TotalTurns, previousTurns, 'count', true),
      trend: report.Daily.map(day => day.Total),
    },
    {
      key: 'activeDealers',
      label: 'Aktif Bayi',
      value: formatCount(current.ActiveDealers),
      caption: 'benzersiz bayi kodu',
      delta: buildDelta(current.ActiveDealers, previous.ActiveDealers, previousTurns, 'count', true),
    },
    {
      key: 'average',
      label: 'Günlük Ort. Soru',
      value: formatDecimal(current.AverageTurnsPerActiveDay),
      caption: 'soru / aktif gün',
      delta: buildDelta(
        current.AverageTurnsPerActiveDay,
        previous.AverageTurnsPerActiveDay,
        previousTurns,
        'count',
        true
      ),
    },
    {
      key: 'noResult',
      label: 'Veri Bulunamadı',
      value: formatPercent(current.NoResultRate),
      caption: 'noResult / değerlendirilen',
      delta: buildDelta(current.NoResultRate, previous.NoResultRate, previousTurns, 'rate', false),
    },
    {
      key: 'fallback',
      label: 'Anlamadı (Fallback)',
      value: formatPercent(current.FallbackRate),
      caption: 'fallback / değerlendirilen',
      delta: buildDelta(current.FallbackRate, previous.FallbackRate, previousTurns, 'rate', false),
    },
    {
      key: 'votes',
      label: 'Kullanıcı Oyu 👍 / 👎',
      value: `${formatCount(current.PositiveVotes)} / ${formatCount(current.NegativeVotes)}`,
      caption: `${formatPercent(current.VoteRate)} oy verme oranı`,
      delta: buildDelta(current.VoteRate, previous.VoteRate, previousTurns, 'rate', true),
    },
  ]
}

export interface HeroModel {
  value: string
  caption: string
  delta: KpiDelta
  trend: number[]
}

export const buildHero = (report: IChatManagementReport): HeroModel => ({
  value: formatPercent(report.Kpis.SuccessRate),
  caption: `Tam başarı (success): ${formatPercent(report.Kpis.FullSuccessRate)}`,
  delta: buildDelta(
    report.Kpis.SuccessRate,
    report.PreviousKpis.SuccessRate,
    report.PreviousKpis.TotalTurns,
    'rate',
    true
  ),
  // Sorusu olmayan gün 0 başarı demek değildir; eğilim çizgisine alınmaz.
  trend: report.Daily.filter(day => day.Total > 0).map(day => day.SuccessRate),
})

export const isBusinessHour = (hour: number): boolean => hour >= 9 && hour <= 17

const barStyle = { borderRadius: 4, borderSkipped: 'start' as const, maxBarThickness: 24 }

export const buildOutcomeChart = (report: IChatManagementReport): ChartData<'bar', number[], string> => ({
  labels: report.Outcomes.map(row => row.Code),
  datasets: [
    {
      label: 'Adet',
      data: report.Outcomes.map(row => row.Count),
      backgroundColor: report.Outcomes.map(row => TREATMENT_COLORS[row.SuccessTreatment] ?? PALETTE.noise),
      ...barStyle,
    },
  ],
})

export const buildCategoryChart = (report: IChatManagementReport): ChartData<'doughnut', number[], string> => ({
  labels: report.Categories.map(row => row.Label),
  datasets: [
    {
      label: 'Adet',
      data: report.Categories.map(row => row.Count),
      backgroundColor: report.Categories.map(row => CATEGORY_COLORS[row.Key] ?? PALETTE.noise),
      borderColor: PALETTE.surface,
      borderWidth: 2,
    },
  ],
})

export const buildDailyVolumeChart = (report: IChatManagementReport): ChartData<'bar', number[], string> => ({
  labels: report.Daily.map(day => formatLocalDate(day.Date)),
  datasets: [
    { label: 'Soru', data: report.Daily.map(day => day.Total), backgroundColor: PALETTE.success, ...barStyle },
  ],
})

const rateLine = (label: string, data: (number | null)[], color: string) => ({
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  borderWidth: 2,
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBorderColor: PALETTE.surface,
  pointBorderWidth: 2,
  tension: 0,
})

/** Sorusu olmayan günün oranı 0 değil "yok"tur: null noktası çizgide boşluk bırakır (spec §4.3). */
export const buildDailyRateChart = (report: IChatManagementReport): ChartData<'line', (number | null)[], string> => ({
  labels: report.Daily.map(day => formatLocalDate(day.Date)),
  datasets: [
    rateLine(
      'Başarı %',
      report.Daily.map(day => (day.Total === 0 ? null : day.SuccessRate)),
      PALETTE.success
    ),
    rateLine(
      'Veri yok %',
      report.Daily.map(day => (day.Total === 0 ? null : day.NoResultRate)),
      PALETTE.noData
    ),
    rateLine(
      'Fallback %',
      report.Daily.map(day => (day.Total === 0 ? null : day.FallbackRate)),
      PALETTE.improvement
    ),
  ],
})

export const buildHourlyChart = (report: IChatManagementReport): ChartData<'bar', number[], string> => ({
  labels: report.Hourly.map(row => formatHour(row.Hour)),
  datasets: [
    {
      label: 'Soru',
      data: report.Hourly.map(row => row.Total),
      backgroundColor: report.Hourly.map(row => (isBusinessHour(row.Hour) ? PALETTE.success : PALETTE.offHours)),
      ...barStyle,
    },
  ],
})

/** Niyet grafiğinin satırları; grafikteki tıklama indeksi bu dizinin indeksidir (Diğer satırı en sonda). */
export const intentChartRows = (report: IChatManagementReport): IIntentRow[] =>
  [...report.IntentPerformance.Top, report.IntentPerformance.Other].filter(row => row.Total > 0)

export const buildIntentChart = (report: IChatManagementReport): ChartData<'bar', number[], string> => {
  const rows = intentChartRows(report)
  const segment = (label: string, data: number[], color: string) => ({
    label,
    data,
    backgroundColor: color,
    borderColor: PALETTE.surface,
    borderWidth: 2,
    maxBarThickness: 24,
    stack: 'intent',
  })

  return {
    labels: rows.map(row => row.Label),
    datasets: [
      segment(
        'Başarılı (veri yok hariç)',
        rows.map(row => row.SuccessCounted - row.NoResult),
        PALETTE.success
      ),
      segment(
        'Veri yok',
        rows.map(row => row.NoResult),
        PALETTE.noData
      ),
      segment(
        'Başarısız',
        rows.map(row => row.Failed),
        PALETTE.improvement
      ),
    ],
  }
}

export const buildSparkline = (values: number[], color: string): ChartData<'line', number[], string> => ({
  labels: values.map((_, index) => String(index + 1)),
  datasets: [
    {
      label: 'Eğilim',
      data: values,
      borderColor: color,
      backgroundColor: color,
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.3,
    },
  ],
})

/** Detaya inme (spec §8.6): boyutların gezinme yolundaki adı. */
export const DIMENSION_LABELS: Record<DrillDimension, string> = {
  outcome: 'Yanıt türü',
  category: 'Kategori',
  intent: 'Araç',
  date: 'Gün',
  hour: 'Saat',
  channel: 'Kanal',
}

export const drillSelection = {
  outcome: (code: string): IDrillSelection => ({ dimension: 'outcome', key: code, label: code }),
  category: (key: string, label: string): IDrillSelection => ({ dimension: 'category', key, label }),
  intent: (code: string, label: string): IDrillSelection => ({ dimension: 'intent', key: code, label }),
  date: (date: string): IDrillSelection => ({ dimension: 'date', key: date, label: formatLocalDate(date) }),
  hour: (hour: number): IDrillSelection => ({ dimension: 'hour', key: String(hour), label: formatHour(hour) }),
  channel: (key: ChannelKey, label: string): IDrillSelection => ({ dimension: 'channel', key, label }),
}
